/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { triggersUpdate } from "../funcs/triggersUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { TriggerInstance } from "../models/components/triggerinstance.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateTriggerInstanceRequest,
  UpdateTriggerInstanceSecurity,
} from "../models/operations/updatetriggerinstance.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateTriggerMutationVariables = {
  request: UpdateTriggerInstanceRequest;
  security?: UpdateTriggerInstanceSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateTriggerMutationData = TriggerInstance;

export type UpdateTriggerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateTriggerInstance triggers
 *
 * @remarks
 * Update a trigger instance.
 */
export function useUpdateTriggerMutation(
  options?: MutationHookOptions<
    UpdateTriggerMutationData,
    UpdateTriggerMutationError,
    UpdateTriggerMutationVariables
  >,
): UseMutationResult<
  UpdateTriggerMutationData,
  UpdateTriggerMutationError,
  UpdateTriggerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateTriggerMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateTrigger(): MutationKey {
  return ["@gram/client", "triggers", "update"];
}

export function buildUpdateTriggerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateTriggerMutationVariables,
  ) => Promise<UpdateTriggerMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateTrigger(),
    mutationFn: function updateTriggerMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateTriggerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(triggersUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
